import type {
	ConfluenceFeatures,
	SupportedCurrentEditions,
	SupportedEditions,
} from '@atlassian/confluence-end-user-upgrade-request-modal';

import { ConfluenceEdition } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';

type TouchpointInfo<E extends SupportedCurrentEditions = SupportedCurrentEditions> = {
	requestedEdition: SupportedEditions;
	requestedFeature: ConfluenceFeatures<E>;
};

/**
 * Generic type that resolves to the touchpoints for a given SupportedCurrentEditions.
 * When no edition is supplied, returns all touchpoints.
 */
type Touchpoints<E extends SupportedCurrentEditions = SupportedCurrentEditions> = Record<
	string,
	TouchpointInfo<E>
>;

const FREE_TOUCHPOINTS: Touchpoints<'free'> = {
	pageInsights: {
		requestedEdition: 'standard',
		requestedFeature: 'pageInsights',
	},
	archiveUpsellDialog: {
		requestedEdition: 'standard',
		requestedFeature: 'archive',
	},
	generalAccessLevelSelector: {
		requestedEdition: 'standard',
		requestedFeature: 'pagePermissions',
	},
	shareAndRestrictDialogUpsell: {
		requestedEdition: 'standard',
		requestedFeature: 'pagePermissions',
	},
	quickSummaryUpsellScreen: {
		requestedEdition: 'premium',
		requestedFeature: 'rovo',
	},
	teamCalendarUpsellScreen: {
		requestedEdition: 'premium',
		requestedFeature: 'calendars',
	},
	'whiteboardCreationPaywall-upfrontDialog': {
		requestedEdition: 'premium',
		requestedFeature: 'whiteboards',
	},
};

const STANDARD_TOUCHPOINTS: Touchpoints<'standard'> = {
	viewSpacePerms: {
		requestedEdition: 'premium',
		requestedFeature: 'copySpacePermissions',
	},
	contentManagerUpsellScreen: {
		requestedEdition: 'premium',
		requestedFeature: 'contentManager',
	},
	adminCenter: {
		requestedEdition: 'premium',
		requestedFeature: 'adminCenter',
	},
	userAccessUpsellScreen: {
		requestedEdition: 'premium',
		requestedFeature: 'inspectPermissions',
	},
	shareAndRestrictDialogInspectPermissionsUpsell: {
		requestedEdition: 'premium',
		requestedFeature: 'inspectPermissions',
	},
	restrictionsDialogInspectPermissionsUpsell: {
		requestedEdition: 'premium',
		requestedFeature: 'inspectPermissions',
	},
	companyHubUpsellScreen: {
		requestedEdition: 'premium',
		requestedFeature: 'companyHub',
	},
	premiumCardsInsertUpsellScreen: {
		requestedEdition: 'premium',
		requestedFeature: 'pageDynamicInsert',
	},
	premiumCarouselInsertUpsellScreen: {
		requestedEdition: 'premium',
		requestedFeature: 'pageDynamicInsert',
	},
	premiumSpotlightInsertUpsellScreen: {
		requestedEdition: 'premium',
		requestedFeature: 'pageDynamicInsert',
	},
	premiumCardsConfigUpsellScreen: {
		requestedEdition: 'premium',
		requestedFeature: 'pageDynamicInsert',
	},
	premiumCarouselConfigUpsellScreen: {
		requestedEdition: 'premium',
		requestedFeature: 'pageDynamicInsert',
	},
	premiumSpotlightConfigUpsellScreen: {
		requestedEdition: 'premium',
		requestedFeature: 'pageDynamicInsert',
	},
	whiteboardsUpsellCtaScreen: {
		requestedEdition: 'premium',
		requestedFeature: 'whiteboardInsert',
	},
	'whiteboardCreationPaywall-upfrontDialog': {
		requestedEdition: 'premium',
		requestedFeature: 'whiteboards',
	},
	'whiteboardCreationPaywall-unsetDialog': {
		requestedEdition: 'premium',
		requestedFeature: 'whiteboards',
	},
	'whiteboardCreationPaywall-viewOnlyButton': {
		requestedEdition: 'premium',
		requestedFeature: 'whiteboards',
	},
	createSpaceDrawer: {
		requestedEdition: 'premium',
		requestedFeature: 'copySpacePermissions',
	},
};

type FreeEligibleTouchpoints = keyof typeof FREE_TOUCHPOINTS;
type StandardEligibleTouchpoints = keyof typeof STANDARD_TOUCHPOINTS;

export type EligibleTouchpoints = FreeEligibleTouchpoints | StandardEligibleTouchpoints;

export const getEligibleTouchpointForEdition = (
	source: string,
	edition: ConfluenceEdition | null,
): TouchpointInfo<SupportedCurrentEditions> | undefined => {
	switch (edition) {
		case ConfluenceEdition.FREE:
			return FREE_TOUCHPOINTS[source];
		case ConfluenceEdition.STANDARD:
			return STANDARD_TOUCHPOINTS[source];
		default:
			return undefined;
	}
};
